import React, { type SyntheticEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { styled, Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';
import { ChevronLeft, Pencil } from 'lucide-react';

import { useGetProjectById } from '../../../hooks';
import { useGetRouteParams } from '../../../hooks/tanstack/useUtilsHook';
import routes from '../../../routes';
import { type ModelType } from '../../../types';
import CollapseDescription from '../../CollapseDescription';
import ModelComparisonView from '../../model/ModelComparisonView';
import {
  type StyledTabProps,
  type StyledTabsProps,
  type TabPanelProps,
} from '../../mui-custom';
import CreateProjectDialog from '../CreateProjectDialog';

import DefaultModelsTable from './DefaultModelsTable';
import ReceivedModelsTable from './ReceivedModelsTable';
import SharedModelsTable from './SharedModelsTable';

export const Tabs = styled((props: StyledTabsProps) => (
  <MuiTabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))({
  gap: '12px !important',
  backgroundColor: '#F5F9FF',

  '& .MuiTabs-indicator': {
    display: 'flex',
    gap: '12px !important',
    justifyContent: 'center',
    backgroundColor: '#F5F9FF',
  },

  '& .MuiTabs-flexContainer': {
    paddingLeft: '48px',
    gap: '24px',
  },

  '& .MuiTabs-indicatorSpan': {
    width: '100%',
    backgroundColor: '#01285F',
  },
});

export const Tab = styled(
  (
    props: StyledTabProps & {
      disabled?: boolean;
    }
  ) => (!props.label ? null : <MuiTab disableRipple {...props} />)
)({
  fontFamily: 'Poppins, sans-serif',
  textTransform: 'none',
  padding: '0px !important',
  minWidth: 'fit-content !important',

  '&.MuiButtonBase-root': {
    padding: '0px !important',
  },
  '&.Mui-selected': {
    color: '#01285F',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'transparent',
  },
});

export const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      className="space-y-6 px-12 py-5"
      role="tabpanel"
      hidden={value !== index}
      id={value}
      {...other}
    >
      {value === index && children}
    </div>
  );
};

export const TABS = ({ isGuestProject }: { isGuestProject: boolean }) => {
  if (isGuestProject) {
    return ['Models', 'Shared', 'Compare Models'];
  }

  return ['Models', 'Shared', 'Received', 'Compare Models'];
};

type TabKey =
  | 'default-models'
  | 'shared-models'
  | 'received-models'
  | 'compare-models';

const TabsEnum = ({ isGuestProject }: { isGuestProject: boolean }) => ({
  'default-models': 0,
  'shared-models': 1,
  'received-models': isGuestProject ? NaN : 2,
  'compare-models': isGuestProject ? 2 : 3,
});

const Overview = () => {
  const [openCreateProjectDialog, setOpenCreateProjectDialog] = useState(false);
  const navigate = useNavigate();

  const { orgId, projectId } = useGetRouteParams();

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const handleGoBack = () => {
    navigate(`${routes.projects.index.replace(':org_id', orgId.toString())}`);
  };

  const handleOpenCreateProjectDialog = () => {
    setOpenCreateProjectDialog(true);
  };

  const handleCloseCreateProjectDialog = () => {
    setOpenCreateProjectDialog(false);
  };

  const isEditable =
    project && project.role === 'project_owner' && !project.is_guest_project;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between">
        <div className="flex items-center gap-4">
          <div
            onClick={handleGoBack}
            className="flex items-center gap-4 cursor-pointer"
          >
            <ChevronLeft size={25} color="#333333" />
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{project?.name}</h1>
              </div>
            </div>
          </div>
          {isEditable && (
            <>
              <Pencil
                size={20}
                color="#333333"
                className="cursor-pointer"
                onClick={handleOpenCreateProjectDialog}
              />
              <CreateProjectDialog
                open={openCreateProjectDialog}
                handleClose={handleCloseCreateProjectDialog}
                primaryActionLabel="Update"
                defaultValues={project}
              />
            </>
          )}
        </div>
      </div>
      <CollapseDescription
        label="Description"
        description={project?.description}
        emptyText="No description provided"
      />
    </div>
  );
};

const ProjectModelsManagement = () => {
  const [index, setIndex] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const type = searchParams.get('type') as ModelType;

  const { orgId, projectId } = useGetRouteParams();

  const { data: project } = useGetProjectById(
    {
      orgId,
      projectId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const tab =
    type === 'shared'
      ? 'shared-models'
      : type === 'received'
        ? 'received-models'
        : searchParams.get('tab');

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setIndex(newValue);

    if (newValue === 0) {
      searchParams.delete('type');
      searchParams.set('tab', 'default-models');
    } else if (newValue === 1) {
      searchParams.set('type', 'shared');
      searchParams.set('tab', 'shared-models');
    } else if (newValue === 2) {
      if (project?.is_guest_project) {
        searchParams.delete('type');
        searchParams.set('tab', 'compare-models');
        setSearchParams(searchParams);
        return;
      }
      searchParams.set('type', 'received');
      searchParams.set('tab', 'received-models');
    } else if (newValue === 3) {
      if (project?.is_guest_project) {
        return;
      }
      searchParams.delete('type');
      searchParams.set('tab', 'compare-models');
    }

    setSearchParams(searchParams);
  };

  useEffect(() => {
    if (!tab) {
      setIndex(0);
      return;
    }

    setIndex(
      TabsEnum({ isGuestProject: !!project?.is_guest_project })[
        tab as TabKey
      ] ?? 0
    );
  }, [tab]);

  return (
    <div className="text-2xl font-bold">
      <Tabs value={index} onChange={handleChange}>
        {TABS({ isGuestProject: !!project?.is_guest_project }).map((report) => (
          <Tab id={report} key={report} label={report} />
        ))}
      </Tabs>
      <CustomTabPanel value={index.toString()} index="0">
        <div className="space-y-4">
          <Overview />
          <DefaultModelsTable />
        </div>
      </CustomTabPanel>
      <CustomTabPanel value={index.toString()} index="1">
        <div className="space-y-4">
          <Overview />
          <SharedModelsTable />
        </div>
      </CustomTabPanel>
      {!project?.is_guest_project && (
        <CustomTabPanel value={index.toString()} index="2">
          <div className="space-y-4">
            <Overview />
            <ReceivedModelsTable />
          </div>
        </CustomTabPanel>
      )}
      <CustomTabPanel
        value={index.toString()}
        index={project?.is_guest_project ? '2' : '3'}
      >
        <ModelComparisonView />
      </CustomTabPanel>
    </div>
  );
};

export default ProjectModelsManagement;
